import React, {useEffect, useState, useCallback} from 'react';
import {Button, Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {toast} from 'react-toastify';
import { errorToast } from '../../utils/errorToast';
import {apiRequest} from '../../utils/api-request';
import './ImageUpload.scss'
import { fileUploadChecks } from '../../utils/fileUploadCheck';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import { getCropAspect } from '../../constants/crop-aspect-ratios';

const ImageUploadComponent = ({ mediaType = 'user-avatar', onSuccess, startImageUrl = '', onImageChange, showImgPreview, setShowImgPreview, isEditing }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(startImageUrl);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [originalImageUrl, setOriginalImageUrl] = useState(null);
    const [originalFileName, setOriginalFileName] = useState(null);
    const [isCropping, setIsCropping] = useState(false);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setValidationError(null);
        const validation = await fileUploadChecks(file);

        if (!validation.valid) {
            setValidationError(validation.error);
            errorToast(validation.error);
            setSelectedFile(null);
            setImagePreviewUrl(null);
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        setOriginalImageUrl(imageUrl);
        setOriginalFileName(file.name);
        setShowCropModal(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropConfirm = async () => {
        try {
            setIsCropping(true);
            const croppedBlob = await getCroppedImg(originalImageUrl, croppedAreaPixels);

            // Extract filename without extension and use it
            const fileNameWithoutExt = originalFileName ? originalFileName.replace(/\.[^/.]+$/, '') : 'image';
            const croppedFileName = `${fileNameWithoutExt}.jpg`;

            const croppedFile = new File([croppedBlob], croppedFileName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
            });

            setSelectedFile(croppedFile);
            setImagePreviewUrl(URL.createObjectURL(croppedFile));
            setShowCropModal(false);

            if (onImageChange) {
                const fakeEvent = { target: { files: [croppedFile] } };
                onImageChange(fakeEvent);
            }

            toast.success('Image cropped successfully');
        } catch (error) {
            console.error('Crop error:', error);
            errorToast(error.message || 'Failed to crop image');
        } finally {
            setIsCropping(false);
        }
    };

    const handleCropCancel = () => {
        setShowCropModal(false);
        setOriginalImageUrl(null);
        setOriginalFileName(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };

    const handleDragLeave = () => {
        setIsDraggingOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDraggingOver(false);

        const file = e.dataTransfer.files[0];
        if (!file) return;

        setValidationError(null);
        const validation = await fileUploadChecks(file);

        if (!validation.valid) {
            setValidationError(validation.error);
            errorToast(validation.error);
            setSelectedFile(null);
            setImagePreviewUrl(null);
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        setOriginalImageUrl(imageUrl);
        setOriginalFileName(file.name);
        setShowCropModal(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            errorToast('No image selected');
            return;
        }

        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('mediaType', mediaType);

        try {
            const response = await toast.promise(
                apiRequest(`/media/${mediaType}`, 'POST', formData),
                {
                    pending: 'Uploading and optimizing image...',
                    success: 'Upload successful',
                }
            );
            onSuccess(response.media);
        } catch (error) {
            console.error('Error uploading image:', error);

            if (!error.response) {
                errorToast('Network error. Please check your connection and try again.');
                return;
            }

            if (error.response?.status === 413) {
                const errorMessage = error.response?.data?.error || 'File size too large. Maximum allowed size is 10MB.';
                errorToast(errorMessage);
            } else if (error.response?.status === 400) {
                const errorMessage = error.response?.data?.error || 'Invalid file or optimization failed. Please try a different image.';
                errorToast(errorMessage);
            } else if (error.response?.status === 500) {
                const errorMessage = 'Server error during upload. Please try again later.';
                errorToast(errorMessage);
            } else {
                const errorMessage = error.response?.data?.error || error.message || `Error uploading ${mediaType}. Please try again.`;
                errorToast(errorMessage);
            }
        }
    };

    useEffect(()=>{
        if (showImgPreview === 'reset'){
            setShowImgPreview(null)
        }else if(!isEditing){
            setImagePreviewUrl(null)
            setSelectedFile(null)
        }
    },[setShowImgPreview, showImgPreview, isEditing])

    useEffect(() => {
        return () => {
            if (originalImageUrl && originalImageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(originalImageUrl);
            }
            if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:') && imagePreviewUrl !== startImageUrl) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
        };
    }, [originalImageUrl, imagePreviewUrl, startImageUrl]);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="position-relative">
            <input
                type="file"
                onChange={handleFileChange}
                className="d-none"
                id="file-input"
                accept="image/*"
            />

            <div
                className={`drag-area p-4 text-center mb-3 ${isDraggingOver && 'border-primary'} ${validationError && 'border-danger'}`}
                onClick={() => document.getElementById('file-input').click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {imagePreviewUrl ? (
                    <img src={imagePreviewUrl} alt="Preview" />
                ) : (
                    <div>
                        <div className="mb-3"><span className="fas fa-plus fa-2x"/></div>
                        Click or drag & drop to upload
                    </div>
                )}
            </div>

            {selectedFile && (
                <div className="mb-2 small">
                    <span className="fas fa-file-image me-2" />
                    {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </div>
            )}

            {validationError && (
                <div className="alert alert-danger py-2 mb-2">
                    <span className="fas fa-exclamation-triangle me-2" />
                    {validationError}
                </div>
            )}

            <Button
                color="primary"
                className="float-end"
                onClick={handleUpload}
                disabled={!selectedFile || validationError}
            >
                Upload Image
            </Button>

            <Modal isOpen={showCropModal} size="lg" toggle={handleCropCancel}>
                <ModalHeader toggle={handleCropCancel}>
                    Crop Image - {getCropAspect(mediaType).label}
                </ModalHeader>
                <ModalBody>
                    <div className="text-muted mb-2 small">
                        {getCropAspect(mediaType).description}
                    </div>
                    <div className="crop-container">
                        {originalImageUrl && (
                            <Cropper
                                image={originalImageUrl}
                                crop={crop}
                                zoom={zoom}
                                aspect={getCropAspect(mediaType).aspect}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        )}
                    </div>
                    <div className="mt-3">
                        <label className="form-label small">Zoom</label>
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="form-range"
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCropCancel} disabled={isCropping}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleCropConfirm} disabled={isCropping}>
                        {isCropping ? 'Cropping...' : 'Confirm Crop'}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ImageUploadComponent;
